import { useSignal } from "@preact/signals";
import { useEffect, useRef } from "preact/hooks";
import { rpc } from "../../rpc";
import { currentUser } from "../../stores/current-user";
import type { Comment, CommentTargetType } from "../../types";
import { IcoMessageCircle, IcoSpinner, IcoTrash } from "../icons";
import { MemberHoverCard } from "./member-hover-card";
import {
  MentionRichTextEditor,
  prefetchMembersForMention,
} from "./mention-rich-text-editor";
import { decodeMentionsToHtml } from "./rich-text-mention-extension";

// ============================================================================
// Types
// ============================================================================

type CommentThreadProps = {
  workspaceId: string;
  targetType: CommentTargetType;
  targetId: string;
  /** Current user's member ID (for showing delete button on own comments) */
  currentMemberId: string;
  /** Whether the thread is read-only (e.g., in mimic mode) */
  readOnly?: boolean;
  /** Callback when comment count changes (for parent to update badges) */
  onCommentCountChange?: (count: number) => void;
  /** Auto-focus the comment input when the thread opens */
  autoFocus?: boolean;
};

type CommentItemProps = {
  comment: Comment;
  isOwn: boolean;
  onDelete?: () => void;
  readOnly?: boolean;
  workspaceId: string;
};

type CommentInputProps = {
  onSubmit: (content: string) => Promise<void>;
  disabled?: boolean;
  workspaceId: string;
  autoFocus?: boolean;
};

// ============================================================================
// CommentItem - Single comment display
// ============================================================================

function CommentItem({
  comment,
  isOwn,
  onDelete,
  readOnly,
  workspaceId,
}: CommentItemProps) {
  const imageError = useSignal(false);

  const formatTime = (date: Date | string) => {
    const d = typeof date === "string" ? new Date(date) : date;
    const now = new Date();
    const diff = now.getTime() - d.getTime();
    const minutes = Math.floor(diff / 60000);
    const hours = Math.floor(diff / 3600000);
    const days = Math.floor(diff / 86400000);

    if (minutes < 1) return "just now";
    if (minutes < 60) return `${minutes}m ago`;
    if (hours < 24) return `${hours}h ago`;
    if (days < 7) return `${days}d ago`;
    return d.toLocaleDateString("en-US", { month: "short", day: "numeric" });
  };

  const initial = comment.authorName.charAt(0).toUpperCase();

  return (
    <div class="group flex gap-3">
      {/* Avatar + author name in single hover card */}
      <MemberHoverCard
        memberId={comment.authorMemberId}
        workspaceId={workspaceId}
        class="flex-shrink-0"
      >
        <div class="cursor-pointer">
          {comment.authorProfilePictureUrl && !imageError.value ? (
            <img
              src={comment.authorProfilePictureUrl}
              alt=""
              class="size-10 rounded-full object-cover"
              onError={() => {
                imageError.value = true;
              }}
            />
          ) : (
            <div class="size-10 rounded-full bg-amber-100 dark:bg-amber-900/30 flex items-center justify-center text-amber-800 dark:text-amber-200 text-sm font-medium">
              {initial}
            </div>
          )}
        </div>
      </MemberHoverCard>

      {/* Content */}
      <div class="flex-1 min-w-0">
        <div class="flex items-center justify-between">
          <p class="text-sm">
            <span class="font-semibold text-text">{comment.authorName}</span>
            <span class="text-text-muted ml-2 text-xs">
              {formatTime(comment.createdAt)}
            </span>
          </p>
          {/* Delete button on the right */}
          {isOwn && !readOnly && onDelete && (
            <button
              type="button"
              onClick={onDelete}
              class="opacity-0 group-hover:opacity-100 text-text-muted hover:text-red-600 dark:hover:text-red-400 transition-all p-1"
              title="Delete"
            >
              <IcoTrash class="size-4" />
            </button>
          )}
        </div>
        <div
          class="text-sm text-text mt-1 break-words [&_p]:m-0 [&_p]:leading-relaxed"
          dangerouslySetInnerHTML={{
            __html: decodeMentionsToHtml(comment.content),
          }}
        />
        {/* Reaction trigger for this comment - like 15five */}
        <button
          type="button"
          class="mt-2 text-text-muted/50 hover:text-text-muted transition-colors"
          title="Add reaction"
        >
          <span class="text-lg">☺</span>
        </button>
      </div>
    </div>
  );
}

// ============================================================================
// CommentInput - Compose new comment
// ============================================================================

function CommentInput({
  onSubmit,
  disabled,
  workspaceId,
  autoFocus,
}: CommentInputProps) {
  const content = useSignal("");
  const isSubmitting = useSignal(false);
  const isFocused = useSignal(false);
  const editorRef = useRef<HTMLDivElement>(null);

  const isDisabled = disabled || isSubmitting.value;

  const handleSubmit = async () => {
    const trimmedContent = content.value.trim();
    if (!trimmedContent || isDisabled) return;

    isSubmitting.value = true;
    try {
      await onSubmit(trimmedContent);
      content.value = "";
    } finally {
      isSubmitting.value = false;
    }
  };

  const handleKeyDown = (e: KeyboardEvent) => {
    // Enter without Shift submits the comment
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSubmit();
    }
  };

  const user = currentUser.value;
  const initial = user?.name?.charAt(0).toUpperCase() ?? "?";

  return (
    <div class="flex items-start gap-3">
      {/* Current user avatar */}
      <div class="size-10 rounded-full flex-shrink-0 flex items-center justify-center overflow-hidden">
        {user?.profilePictureUrl ? (
          <img
            src={user.profilePictureUrl}
            alt=""
            class="size-10 rounded-full object-cover"
          />
        ) : (
          <div class="size-10 rounded-full bg-amber-100 dark:bg-amber-900/30 flex items-center justify-center text-amber-800 dark:text-amber-200 text-sm font-medium">
            {initial}
          </div>
        )}
      </div>

      <div class="flex-1">
        <div
          ref={editorRef}
          onKeyDown={handleKeyDown}
          onFocusCapture={() => {
            isFocused.value = true;
          }}
          onBlurCapture={(e) => {
            // Only blur if focus moves outside the editor container
            if (!editorRef.current?.contains(e.relatedTarget as Node)) {
              isFocused.value = false;
            }
          }}
          class={`bg-surface border border-border rounded-lg px-3 py-2.5 text-sm focus-within:border-text-muted transition-colors [&_.tiptap]:min-h-[1.5em] [&_.tiptap]:outline-none ${
            isDisabled ? "opacity-50 pointer-events-none" : ""
          }`}
        >
          <MentionRichTextEditor
            value={content.value}
            onChange={(val) => {
              content.value = val;
            }}
            workspaceId={workspaceId}
            placeholder="Add a comment"
            autoFocus={autoFocus}
          />
        </div>
        {(isFocused.value || content.value.trim()) && (
          <div class="mt-2 flex items-center gap-3">
            <button
              type="button"
              onClick={handleSubmit}
              disabled={isDisabled || !content.value.trim()}
              class="px-4 py-1.5 rounded-md text-sm font-medium bg-[var(--brand-600)] text-white hover:bg-[var(--brand-700)] disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
            >
              {isSubmitting.value ? (
                <IcoSpinner class="size-4 animate-spin" />
              ) : (
                "Post"
              )}
            </button>
            <span class="text-xs text-text-subtle">
              Participants will be notified
            </span>
          </div>
        )}
      </div>
    </div>
  );
}

// ============================================================================
// CommentThread - Full comment thread with list and input
// ============================================================================

export function CommentThread({
  workspaceId,
  targetType,
  targetId,
  currentMemberId,
  readOnly = false,
  onCommentCountChange,
  autoFocus,
}: CommentThreadProps) {
  const comments = useSignal<Comment[]>([]);
  const isLoading = useSignal(true);
  const error = useSignal<string | null>(null);

  // Prefetch members for instant @mention suggestions
  useEffect(() => {
    if (!readOnly) {
      prefetchMembersForMention(workspaceId);
    }
  }, [workspaceId, readOnly]);

  // Load comments
  useEffect(() => {
    async function load() {
      isLoading.value = true;
      error.value = null;
      try {
        const result = await rpc.comments.list({
          workspaceId,
          targetType,
          targetId,
        });
        comments.value = result.comments;
        onCommentCountChange?.(result.comments.length);
      } catch (err) {
        error.value = "Failed to load comments";
        console.error("Failed to load comments:", err);
      } finally {
        isLoading.value = false;
      }
    }
    load();
  }, [workspaceId, targetType, targetId]);

  const handleAddComment = async (content: string) => {
    try {
      const result = await rpc.comments.create({
        workspaceId,
        targetType,
        targetId,
        content,
      });
      const newComments = [...comments.value, result.comment];
      comments.value = newComments;
      onCommentCountChange?.(newComments.length);
    } catch (err) {
      console.error("Failed to add comment:", err);
      throw err;
    }
  };

  const handleDeleteComment = async (commentId: string) => {
    try {
      await rpc.comments.remove({ commentId });
      const newComments = comments.value.filter((c) => c.id !== commentId);
      comments.value = newComments;
      onCommentCountChange?.(newComments.length);
    } catch (err) {
      console.error("Failed to delete comment:", err);
    }
  };

  if (isLoading.value) {
    return (
      <div class="py-4 flex justify-center">
        <IcoSpinner class="size-5 animate-spin text-text-muted" />
      </div>
    );
  }

  if (error.value) {
    return (
      <div class="py-4 text-center text-sm text-red-600 dark:text-red-400">
        {error.value}
      </div>
    );
  }

  return (
    <div class="space-y-4">
      {/* Comments list */}
      {comments.value.length > 0 && (
        <div class="space-y-4">
          {comments.value.map((comment) => (
            <CommentItem
              key={comment.id}
              comment={comment}
              isOwn={comment.authorMemberId === currentMemberId}
              onDelete={() => handleDeleteComment(comment.id)}
              readOnly={readOnly}
              workspaceId={workspaceId}
            />
          ))}
        </div>
      )}

      {/* Input */}
      {!readOnly && (
        <CommentInput
          onSubmit={handleAddComment}
          workspaceId={workspaceId}
          autoFocus={autoFocus}
        />
      )}
    </div>
  );
}

// ============================================================================
// CommentButton - Button to toggle comment thread visibility
// ============================================================================

type CommentButtonProps = {
  count: number;
  isOpen: boolean;
  onClick: () => void;
};

export function CommentButton({ count, isOpen, onClick }: CommentButtonProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      class={`inline-flex items-center gap-1.5 px-2 py-1 rounded-md text-sm transition-colors ${
        isOpen
          ? "bg-[var(--brand-100)] text-[var(--brand-700)]"
          : "text-text-muted hover:text-text hover:bg-surface-muted"
      }`}
    >
      <IcoMessageCircle class="size-4" />
      <span class="text-xs">{count > 0 ? count : "Comment"}</span>
    </button>
  );
}
